// components/admin/sidebarConfig.ts
import type { LucideIcon } from 'lucide-react';
import {
  LayoutDashboard, Building2, UserCircle, Contact, Files, Gauge, Mail, Wrench,
  TicketCheck, CalendarDays, FileText, Receipt, ShieldCheck, Zap,
  Briefcase, KeyRound, Wallet, Settings2, Users, Hourglass, ClipboardCheck, Timer, Send,
} from 'lucide-react';

export type SidebarItem = {
  /** Muss zu portal_modules.id passen, wenn das Modul existiert */
  id: string;
  label: string;
  icon: LucideIcon;
  path: string;
  /** Wenn true: ausgegraut, nicht klickbar, BALD-Badge */
  comingSoon?: boolean;
  /** Wenn true: Item ist nur für Super Admin sichtbar */
  superAdminOnly?: boolean;
};

export type SidebarCategoryId = 'verwaltung' | 'vermietung' | 'buchhaltung' | 'personal' | 'system';

export type SidebarCategory = {
  id: SidebarCategoryId;
  label: string;
  /** Icon für Collapsed-Modus (Trenner) */
  icon: LucideIcon;
  items: SidebarItem[];
};

/** Top-Level-Items, die NICHT in einer Kategorie liegen (z. B. Dashboard) */
export const TOP_LEVEL_ITEMS: SidebarItem[] = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/admin/dashboard' },
];

export const SIDEBAR_CATEGORIES: SidebarCategory[] = [
  {
    id: 'verwaltung',
    label: 'Verwaltung',
    icon: Briefcase,
    items: [
      { id: 'objekte',     label: 'Immobilien',    icon: Building2,  path: '/admin/objekte' },
      { id: 'eigentuemer',   label: 'Eigentümer',    icon: UserCircle, path: '/admin/eigentuemer' },
      { id: 'dienstleister', label: 'Dienstleister', icon: Wrench,     path: '/admin/dienstleister' },
      { id: 'verwalter',   label: 'Verwalter',     icon: Users,      path: '/admin/verwalter',  comingSoon: true },
      { id: 'kontakte',    label: 'Kontakte',      icon: Contact,    path: '/admin/kontakte' },
      { id: 'dokumente',   label: 'Dokumente',     icon: Files,      path: '/admin/dokumente' },
      { id: 'zaehler',     label: 'Zähler',        icon: Gauge,      path: '/admin/zaehler' },
      { id: 'emails',      label: 'E-Mails',       icon: Mail,       path: '/admin/emails' },
      // #586 (Björn): ein zentraler Ort für Schreiben – Neues Schreiben, Druckstapel, Verlauf
      { id: 'schreiben',   label: 'Schreiben',     icon: Send,       path: '/admin/schreiben' },
      { id: 'historie',    label: 'Historie',      icon: Hourglass,  path: '/admin/historie',   comingSoon: true },
    ],
  },
  {
    id: 'vermietung',
    label: 'Vermietung',
    icon: KeyRound,
    items: [
      { id: 'mieter',                       label: 'Mieter',                       icon: Users,        path: '/admin/mieter' },
      { id: 'vertraege',                    label: 'Mietverträge',                 icon: FileText,     path: '/admin/vertraege' },
      { id: 'mietverhaeltnisse',            label: 'Mietverhältnisse',             icon: FileText,     path: '/admin/mietverhaeltnisse',     comingSoon: true },
      { id: 'mieterselbstauskunft',         label: 'Mieterselbstauskunft',         icon: FileText,     path: '/admin/mieterselbstauskunft' },
      { id: 'serienmail',                   label: 'Serienmail',                   icon: Mail,         path: '/admin/serienmail' },
      { id: 'mietersuche',                  label: 'Mietersuche',                  icon: FileText,     path: '/admin/mietersuche',           comingSoon: true },
      { id: 'leerstand',                    label: 'Leerstand',                    icon: FileText,     path: '/admin/leerstand',             comingSoon: true },
      { id: 'einauszuege',                  label: 'Ein-/Auszüge',                 icon: FileText,     path: '/admin/einauszuege',           comingSoon: true },
      // Kennung MUSS dem Pfadsegment entsprechen (wie bei allen anderen Einträgen):
      // App.tsx leitet die aktuelle Ansicht aus dem Pfad ab und vergleicht sie mit
      // allowed_modules — bei abweichender Kennung wird die Rolle „team" trotz
      // Freischaltung weggeleitet, die Sidebar markiert nie aktiv und die Kopfzeile
      // zeigt keinen Bereichsnamen. Auch portal_modules.id lautet 'uebergabeprotokolle'.
      { id: 'uebergabeprotokolle',          label: 'Übergabeprotokolle',           icon: ClipboardCheck, path: '/admin/uebergabeprotokolle' },
      { id: 'kuendigungen',                 label: 'Kündigungen',                  icon: FileText,     path: '/admin/kuendigungen',          comingSoon: true },
      { id: 'nachtraege',                   label: 'Nachträge',                    icon: FileText,     path: '/admin/nachtraege',            comingSoon: true },
      { id: 'tickets',                      label: 'Tickets',                      icon: TicketCheck,  path: '/admin/tickets' },
      { id: 'kalender',                     label: 'Kalender',                     icon: CalendarDays, path: '/admin/kalender' },
    ],
  },
  {
    id: 'buchhaltung',
    label: 'Buchhaltung',
    icon: Wallet,
    items: [
      { id: 'rechnungen',     label: 'Rechnungen',     icon: Receipt,  path: '/admin/rechnungen' },
      { id: 'sollstellung',   label: 'Sollstellung',   icon: Receipt,  path: '/admin/sollstellung',   comingSoon: true },
      { id: 'offene-posten',  label: 'Offene Posten',  icon: Receipt,  path: '/admin/offene-posten',  comingSoon: true },
      { id: 'zahlungen',      label: 'Zahlungen',      icon: Receipt,  path: '/admin/zahlungen',      comingSoon: true },
      { id: 'kaution',        label: 'Kaution',        icon: Receipt,  path: '/admin/kaution',        comingSoon: true },
      { id: 'mahnwesen',      label: 'Mahnwesen',      icon: Receipt,  path: '/admin/mahnwesen',      comingSoon: true },
    ],
  },
  {
    // Eigener Bereich statt Untermenü der Einstellungen: Zeiterfassung und Personal
    // sind Tagesgeschäft für ALLE Mitarbeitenden, nicht Verwaltung der Software.
    id: 'personal',
    label: 'Personal',
    icon: Users,
    items: [
      { id: 'zeiterfassung',   label: 'Zeiterfassung',   icon: Timer,        path: '/admin/zeiterfassung' },
      { id: 'abwesenheiten',   label: 'Abwesenheiten',   icon: CalendarDays, path: '/admin/abwesenheiten' },
      { id: 'personalakte',    label: 'Personalakte',    icon: Contact,      path: '/admin/personalakte' },
      { id: 'monatsabschluss', label: 'Monatsabschluss', icon: ShieldCheck,  path: '/admin/monatsabschluss' },
    ],
  },
  {
    id: 'system',
    label: 'System',
    icon: Settings2,
    items: [
      { id: 'team',        label: 'Team',           icon: ShieldCheck, path: '/admin/team' },
      { id: 'super-admin', label: 'Control Center', icon: Zap,         path: '/admin/super-admin', superAdminOnly: true },
    ],
  },
];

/** localStorage Key für die zuletzt im Panel gewählte Kategorie */
export const SELECTED_CATEGORY_STORAGE_KEY = 'sidebar_selected_category';
